// ZReport.jsx (X-Report Look & Feel + Auto Preview/Auto Download/Auto Logout)
// ✅ Reads ctx from sessionStorage (__ZREPORT_CTX__)
// ✅ Auto fetches report (ALL records; no paging UI)
// ✅ Auto opens modal preview
// ✅ Auto downloads PDF automatically
// ✅ Auto logs out after download OR when modal is closed (shift cleared)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import ApiService from '../../../services/apiService';
import userManagement from '../../../services/userManagement';

// ✅ X-Report template assets
import './report.css'; // ensure correct path
import Logo from '../../../assets/images/Pro_Logo.png'; // ensure correct path

const LOGOUT_REDIRECT_URL = 'https://app.pro360erp.com/';
const ZREPORT_CTX_KEY = '__ZREPORT_CTX__';

// ---- helpers ----
const safeParse = (v, fb = null) => {
  try {
    return JSON.parse(v);
  } catch {
    return fb;
  }
};

const groupBySection = (rows = []) =>
  rows.reduce((acc, r) => {
    const sec = r?.section || 'OTHER';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(r);
    return acc;
  }, {});

const clearAllClientStorage = () => {
  try {
    localStorage.clear();
  } catch (err) {
    window.location.href = LOGOUT_REDIRECT_URL;
  }
  try {
    sessionStorage.clear();
  } catch (err) {
    window.location.href = LOGOUT_REDIRECT_URL;
  }

  // Cache Storage
  try {
    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => {
          window.location.href = LOGOUT_REDIRECT_URL;
        });
    }
  } catch (err) {
    window.location.href = LOGOUT_REDIRECT_URL;
  }

  // IndexedDB (best effort)
  try {
    if (indexedDB?.databases) {
      indexedDB
        .databases()
        .then((dbs) => {
          dbs.forEach((db) => {
            if (db?.name) {
              try {
                indexedDB.deleteDatabase(db.name);
              } catch (err) {
                window.location.href = LOGOUT_REDIRECT_URL;
              }
            }
          });
        })
        .catch(() => {
          window.location.href = LOGOUT_REDIRECT_URL;
        });
    }
  } catch {}

  // Cookies (non-HttpOnly)
  try {
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch (err) {
    window.location.href = LOGOUT_REDIRECT_URL;
  }
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/** Normalize API response: day open may appear at root, under result object, or as a REPORT INFO row */
const extractDayOpenFromResponse = (apiData) => {
  if (!apiData || typeof apiData !== 'object') return null;

  const pick = (o) => {
    if (!o || typeof o !== 'object') return null;
    const v =
      o.dayOpenDateTime ??
      o.dayOpenedAt ??
      o.dayOpenDateAndTime ??
      o.dayOpenDate ??
      o.openDateTime ??
      o.openDate ??
      o.sessionOpenDate ??
      o.daySessionOpenDate ??
      o.daySessionOpenedAt ??
      o.counterDayOpenDateTime;
    return v != null && String(v).trim() !== '' ? v : null;
  };

  let v = pick(apiData);
  if (v) return v;

  const r = apiData.result;
  if (r && typeof r === 'object' && !Array.isArray(r)) {
    v = pick(r);
    if (v) return v;
  }

  if (Array.isArray(r)) {
    const metaRow = r.find((row) => {
      const d = String(row?.detail || row?.category || '').toLowerCase();
      const s = String(row?.section || '').toLowerCase();
      return d.includes('day open') || d.includes('opened') || s.includes('day open');
    });
    if (metaRow && (metaRow.amount != null || metaRow.value != null)) {
      return metaRow.amount ?? metaRow.value;
    }
  }

  return null;
};

/** Parse `YYYY-MM-DD HH:mm:ss` or ISO strings reliably for display */
const parseApiDateTime = (str) => {
  if (str == null || str === '') return null;
  const t = String(str).trim();
  const withT = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(t) ? t.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T') : t;
  const d = new Date(withT);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTimeNice = (d) =>
  d.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

const formatDayOpenDisplay = (raw) => {
  if (raw == null || raw === '') return null;
  const d = parseApiDateTime(raw) ?? new Date(raw);
  if (!Number.isNaN(d.getTime())) return formatDateTimeNice(d);
  return String(raw);
};

/**
 * Z report rows often send `detail` like "Open Time : 2026-04-08 15:15:53" — format the datetime part.
 */
const formatZReportDetailLabel = (detail) => {
  if (detail == null || detail === '') return '-';
  const s = String(detail);
  const idx = s.indexOf(':');
  if (idx === -1) return s;
  const label = s.slice(0, idx).trim();
  const value = s.slice(idx + 1).trim();
  if (!value) return s;
  const d = parseApiDateTime(value);
  if (d) return `${label}: ${formatDateTimeNice(d)}`;
  return s;
};

const isCounterDetailsSection = (section) => /^COUNTER\s+DETAILS$/i.test(String(section || '').trim());

/** Pull open/close strings from API COUNTER DETAILS rows (used for REPORT INFO header; section is hidden below) */
const extractOpenCloseFromCounterDetailRows = (rows = []) => {
  let open = null;
  let close = null;
  for (const row of rows) {
    if (!isCounterDetailsSection(row?.section)) continue;
    const det = String(row?.detail || '');
    const idx = det.indexOf(':');
    if (idx === -1) continue;
    const label = det.slice(0, idx).toLowerCase();
    const value = det.slice(idx + 1).trim();
    if (!value) continue;
    if (label.includes('open')) open = value;
    if (label.includes('close')) close = value;
  }
  return { open, close };
};

/** Strip COUNTER DETAILS from grouped report — times show only under REPORT INFO */
const filterOutCounterDetailsRows = (rows = []) => rows.filter((row) => !isCounterDetailsSection(row?.section));

/** counterId in query → API may return these at root or on `result` object */
const pickCounterSessionTimes = (o) => {
  if (!o || typeof o !== 'object') return { open: null, close: null };
  const open = o.CounteropeningTime ?? o.counterOpeningTime ?? o.CounterOpeningTime ?? o.counteropeningTime ?? null;
  const close = o.counterClosingTime ?? o.CounterClosingTime ?? o.counterclosingTime ?? null;
  const norm = (v) => (v != null && String(v).trim() !== '' ? v : null);
  return { open: norm(open), close: norm(close) };
};

const extractCounterTimesFromResponse = (apiData) => {
  if (!apiData || typeof apiData !== 'object') return { open: null, close: null };
  let { open, close } = pickCounterSessionTimes(apiData);
  if (open || close) return { open, close };
  const r = apiData.result;
  if (r && typeof r === 'object' && !Array.isArray(r)) {
    return pickCounterSessionTimes(r);
  }
  return { open: null, close: null };
};

const ZReport = () => {
  const navigate = useNavigate();
  const apiService = useMemo(() => new ApiService(), []);
  const printRef = useRef(null);

  const autoFlowStarted = useRef(false);
  const autoDownloaded = useRef(false);
  const autoLoggedOut = useRef(false);

  const [loadingReport, setLoadingReport] = useState(false);
  const [emp, setEmp] = useState(null);

  // ctx from sessionStorage (sent by DayCloseForm)
  const [ctx, setCtx] = useState(null);

  // report
  const [groupedData, setGroupedData] = useState({});
  /** Raw value from GET_Z_REPORT when CounterId/counterId is sent (day open date/time) */
  const [dayOpenRaw, setDayOpenRaw] = useState(null);
  /** CounteropeningTime / counterClosingTime from GET_Z_REPORT when counterId is sent */
  const [counterOpeningRaw, setCounterOpeningRaw] = useState(null);
  const [counterClosingRaw, setCounterClosingRaw] = useState(null);

  // modal
  const [showModal, setShowModal] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(Logo);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userCompanies');
      const companies = raw ? JSON.parse(raw) : [];

      const logoUrl = companies?.[0]?.logo;
      if (logoUrl) setCompanyLogo(logoUrl);
    } catch (e) {
      // keep fallback if parsing fails
      setCompanyLogo(Logo);
    }
  }, []);
  // date range
  const todayISODate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [fromDate, setFromDate] = useState(todayISODate());
  const [toDate, setToDate] = useState(todayISODate());

  // ✅ FIX: token must be string, not async memo
  const token = useMemo(() => localStorage.getItem('Token'), []);

  const doLogoutNow = () => {
    if (autoLoggedOut.current) return;
    autoLoggedOut.current = true;
    clearAllClientStorage();
    window.location.href = LOGOUT_REDIRECT_URL;
  };

  // read ctx on mount
  useEffect(() => {
    const raw = sessionStorage.getItem(ZREPORT_CTX_KEY);
    if (!raw) return;

    const parsed = safeParse(raw, null);
    if (!parsed) return;

    setCtx(parsed);

    if (parsed?.fromDate) setFromDate(parsed.fromDate);
    if (parsed?.toDate) setToDate(parsed.toDate);
  }, []);

  // effective ids: MUST come from ctx (dayclose redirected)
  const shiftId = useMemo(() => (ctx?.shiftId != null ? Number(ctx.shiftId) : null), [ctx]);
  const counterId = useMemo(() => (ctx?.counterId != null ? Number(ctx.counterId) : null), [ctx]);

  // load employee by token userId
  useEffect(() => {
    const loadEmp = async () => {
      try {
        if (!token) return;
        const userId = await userManagement.getUserId(token);
        if (!userId) return;

        const res = await apiService.getApiParamater('GET_EMPLOYEE', `?UserId=${userId}`);
        setEmp(res?.data?.result?.[0] || null);
      } catch (err) {
        console.error(err);
      }
    };

    loadEmp();
  }, [apiService, token]);

  // backend start/end format
  const makeStartEnd = (from, to) => {
    const s = `${from}  00:00:00`;
    const e = `${to}  23:59:00`;
    return { s, e };
  };

  const reportMeta = useMemo(() => {
    const d = new Date();
    const printedAt = d.toLocaleString('en-GB');
    const dayOpenDisplay = formatDayOpenDisplay(dayOpenRaw) || '-';
    const counterOpeningDisplay = formatDayOpenDisplay(counterOpeningRaw) || '-';
    const counterClosingDisplay = formatDayOpenDisplay(counterClosingRaw) || '-';
    return {
      printedAt,
      shiftId: shiftId ?? '-',
      counterId: counterId ?? '-',
      dayOpenDisplay,
      counterOpeningDisplay,
      counterClosingDisplay,
      employee: emp?.employeeName || emp?.employee_Name || emp?.name || emp?.fullName || '-',
      branch: emp?.branchName || emp?.BranchName || emp?.branch?.name || '-',
      restaurant: emp?.restaurantName || emp?.RestaurantName || emp?.restaurant?.name || '-',
    };
  }, [emp, shiftId, counterId, dayOpenRaw, counterOpeningRaw, counterClosingRaw]);

  // Add REPORT INFO section like X Report
  const reportInfoRows = useMemo(() => {
    return [
      { section: 'REPORT INFO', detail: 'Printed At', amount: reportMeta.printedAt },
      { section: 'REPORT INFO', detail: 'Day open (date & time)', amount: reportMeta.dayOpenDisplay },
      { section: 'REPORT INFO', detail: 'Counter opening time', amount: reportMeta.counterOpeningDisplay },
      { section: 'REPORT INFO', detail: 'Counter closing time', amount: reportMeta.counterClosingDisplay },
      { section: 'REPORT INFO', detail: 'From', amount: fromDate },
      { section: 'REPORT INFO', detail: 'To', amount: toDate },
      { section: 'REPORT INFO', detail: 'Shift #', amount: reportMeta.shiftId },
      { section: 'REPORT INFO', detail: 'Till #', amount: reportMeta.counterId },
      { section: 'REPORT INFO', detail: 'Cashier', amount: reportMeta.employee },
      { section: 'REPORT INFO', detail: 'Branch', amount: reportMeta.branch },
      { section: 'REPORT INFO', detail: 'Restaurant', amount: reportMeta.restaurant },
    ];
  }, [reportMeta, fromDate, toDate]);

  const groupedForReceipt = useMemo(() => {
    const merged = { ...groupedData };
    merged['REPORT INFO'] = reportInfoRows;
    return merged;
  }, [groupedData, reportInfoRows]);

  const orderedSections = useMemo(() => {
    const keys = Object.keys(groupedForReceipt || {});
    if (!keys.length) return [];
    const rest = keys.filter((k) => k !== 'REPORT INFO' && !isCounterDetailsSection(k)).sort();
    return ['REPORT INFO', ...rest.filter((k) => k in groupedForReceipt)];
  }, [groupedForReceipt]);

  const formatAmountForRow = (sectionName, rawAmount) => {
    if (sectionName === 'REPORT INFO') {
      const s = rawAmount == null ? '' : String(rawAmount).trim();
      if (!s) return '-';
      if (/\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(s)) {
        const d = parseApiDateTime(s);
        if (d) return formatDateTimeNice(d);
      }
      return s;
    }
    const n = Number(rawAmount ?? 0);
    return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ✅ Fetch Z report (ALL RECORDS)
  // If backend supports "PageSize=0" or "PageSize=999999" use it.
  // Using a big PageSize is the safest without changing backend.
  const fetchZReport = async ({ openModal = true } = {}) => {
    try {
      setLoadingReport(true);
      setDayOpenRaw(null);
      setCounterOpeningRaw(null);
      setCounterClosingRaw(null);

      if (!token) {
        toast.error('Token missing');
        return false;
      }

      const userId = await userManagement.getUserId(token);
      if (!userId) {
        toast.error('UserId missing');
        return false;
      }

      if (!shiftId || !counterId) {
        toast.error('shiftId/counterId missing from ctx');
        return false;
      }

      const { s: StartDate, e: EndDate } = makeStartEnd(fromDate, toDate);

      const params = new URLSearchParams();
      params.append('StartDate', StartDate);
      params.append('EndDate', EndDate);
      params.append('UserId', String(userId));
      params.append('ShiftId', String(shiftId));
      params.append('CounterId', String(counterId));
      params.append('counterId', String(counterId));

      const branchId = emp?.res_Branch_Id || emp?.branchId || emp?.branch?.id || '';
      if (branchId) params.append('branchId', String(branchId));
      if (emp?.id != null) params.append('EmployeeId', String(emp.id));

      // ✅ fetch all
      params.append('PageNo', '1');
      params.append('PageSize', '100000'); // adjust if needed

      const res = await apiService.getApiParamater('GET_Z_REPORT', `?${params.toString()}`);

      const payload = res?.data;
      const dayOpen = extractDayOpenFromResponse(payload);
      setDayOpenRaw(dayOpen != null ? dayOpen : null);

      const { open: cOpen, close: cClose } = extractCounterTimesFromResponse(payload);

      let rows = payload?.result || [];
      if (!Array.isArray(rows)) {
        rows = [];
      }

      const fromCounterDetailRows = extractOpenCloseFromCounterDetailRows(rows);
      setCounterOpeningRaw(cOpen ?? fromCounterDetailRows.open ?? null);
      setCounterClosingRaw(cClose ?? fromCounterDetailRows.close ?? null);

      const rowsForBody = filterOutCounterDetailsRows(rows);

      if (!rows.length) {
        toast.info('No data found for Z Report');
        setGroupedData({});
        if (openModal) setShowModal(true);
        return true;
      }

      setGroupedData(groupBySection(rowsForBody));
      if (openModal) setShowModal(true);
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch Z Report');
      return false;
    } finally {
      setLoadingReport(false);
    }
  };

  // Print to PDF (returns true/false)
  const handlePrintPDF = async () => {
    if (!printRef.current) return false;

    try {
      const el = printRef.current;

      const canvas = await html2canvas(el, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = position - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Z-Report_${fromDate}_to_${toDate}.pdf`);
      toast.success('Z Report downloaded');
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Print failed (browser may block auto download)');
      return false;
    }
  };

  // ✅ AUTO FLOW ALWAYS:
  // preview -> download -> logout
  useEffect(() => {
    const runAuto = async () => {
      // always auto run when redirected from dayclose
      // if ctx missing, user can still manually preview
      if (!ctx) return;
      if (autoFlowStarted.current) return;

      // wait for emp load because we attach branch params
      if (!emp) return;

      autoFlowStarted.current = true;

      // 1) fetch + open modal
      const ok = await fetchZReport({ openModal: true });
      if (!ok) {
        await wait(600);
        doLogoutNow();
        return;
      }

      // 2) wait for modal paint
      await wait(800);

      // 3) auto download once
      if (!autoDownloaded.current) {
        autoDownloaded.current = true;
        await handlePrintPDF();
      }

      // 4) logout
      await wait(500);
      doLogoutNow();
    };

    runAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, emp]);

  // On closing popup: logout
  const modalclose = () => {
    setShowModal(false);
    doLogoutNow();
  };

  return (
    <div className="p-5" style={{ background: '#fff', minHeight: '100vh' }}>
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Minimal manual controls (optional) */}
      <div className="row mb-3">
        <div className="col-3">
          <label className="form-label">From Date</label>
          <input className="form-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="col-3">
          <label className="form-label">To Date</label>
          <input className="form-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="col-3 d-flex align-items-end">
          <button className="refbtn w-100" onClick={() => fetchZReport({ openModal: true })} disabled={loadingReport}>
            {loadingReport ? 'Loading…' : 'Preview'}
          </button>
        </div>

        <div className="col-3 d-flex align-items-end">
          <button
            className="btn btn-danger w-100"
            onClick={() => {
              doLogoutNow();
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={modalclose}
        backdrop="static" // user cannot close by clicking outside
        keyboard={false}
      >
        <Modal.Header closeButton>
          <div className="receipt-logo center">Z Report</div>
        </Modal.Header>

        <Modal.Body>
          <div ref={printRef}>
            <div className="receipt-container">
              <div className="receipt-header">
                <div className="receipt-logo">
                  <img src={companyLogo} alt="Logo" style={{ maxWidth: '150px', maxHeight: '80px' }} />
                </div>
                <div className="receipt-date">{new Date().toLocaleDateString('en-GB')}</div>
              </div>

              {orderedSections.map((sectionName) => (
                <div key={sectionName} className="section-block">
                  <div className="section-title">{sectionName.toUpperCase()}</div>
                  <hr />
                  {groupedForReceipt[sectionName]?.map((item, index) => {
                    const label = formatZReportDetailLabel(item?.detail || item?.category || '-');
                    const val = formatAmountForRow(sectionName, item?.amount);
                    return (
                      <div key={index} className="receipt-row">
                        <span>{label}</span>
                        <span>{val}</span>
                      </div>
                    );
                  })}
                </div>
              ))}

              {!orderedSections.length && <div className="text-muted">No data to display.</div>}

              <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                This is a system generated report.
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="center w-100">
            <button
              className="refbtn w-100 center"
              onClick={async () => {
                await handlePrintPDF();
                await wait(500);
                doLogoutNow();
              }}
              disabled={loadingReport}
            >
              Print PDF
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ZReport;
